import React from 'react';
import Head from 'next/head';
import { Container, Box, Divider, Grid, Typography, Link } from '@mui/material';
import { makeStyles } from '@mui/styles';
import MainContent from 'components/Poster/MainContent';
import { getEnv, scrollToElement } from 'utils';
import { LandingPageContextEnum } from 'enums';
import Image from 'next/image';
import EmailFormVariant from 'components/EmailFormVariant';

const useStyles = makeStyles((theme) => ({
  '@global': {
    img: {
      maxWidth: '100%',
      maxHeight: '100%',
    },
  },
  '@keyframes blinker': {
    '50%': {
      opacity: 0,
    },
  },
  primaryBg: {
    backgroundColor: '#311171',
  },
  lightBg: {
    backgroundColor: '#f1f1f1',
  },
  brandLogo: {
    width: 130,
  },
  headerTitle: {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
  },
  headerList: {
    listStylePosition: 'inside',
    paddingLeft: 0,
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.h6.lineHeight,
    marginBottom: theme.spacing(6),
  },
  headerButton: {
    padding: theme.spacing(2.5, 4.5),
    marginTop: theme.spacing(3),
    color: theme.palette.common.white,
    borderColor: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
    borderRadius: 40,
  },
  sectionImage: {
    display: 'block',
    margin: '0 auto',
    width: 600,
  },
  likeMagicBox: {
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'center',
    },
  },
  likeMagicBoxItem: {
    flex: 1,
    textAlign: 'center',
    color: theme.palette.common.white,

    '& img': {
      display: 'block',
      width: 250,
      margin: '0 auto',
    },

    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
    },
  },
  verticalDivider: {
    backgroundColor: theme.palette.grey[50],
    margin: theme.spacing(0, 3),
  },
  blink: {
    animation: '$blinker 500ms linear',
  },
}));

export default function PosterMakerPage() {
  const classes = useStyles();

  function scrollActionClick() {
    const headerElement = document.getElementById('header');

    scrollToElement(headerElement);
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is a scientific poster?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A scientific poster or a research poster is an effective tool to communicate your science and to present information about your research. The main goal of these posters is to summarize the information and show it in a structured and visualized way, thus enabling you to better interact with your audience in fewer words. An online scientific poster maker automates much of the designing and minimizes hard work to allow students, researchers and scientists to easily make stunning scientific posters without prior design experience.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is scientific poster in research?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Scientific poster is an infographic that contains all the main information about your research paper. Like magic, you can easily create one using Poster Maker. Click here and start creating now.',
        },
      },
      {
        '@type': 'Question',
        name: 'Why are scientific posters used?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'IScientific posters are used mostly because they are a great visual solution to present all the main information about your research paper in just one place.',
        },
      },
      {
        '@type': 'Question',
        name: 'What makes a good science poster?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A good science poster must contain all the main information about your research paper, such as: introduction, methodology, major topics, conclusion, references, and images.',
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>Scientific Poster Maker: Create Academic Research Posters | Mind the Graph</title>

        <meta
          name="description"
          content="Scientific poster maker to present your research with free designs, templates and guides. Professional research poster maker and academic poster maker by Mind the Graph."
        />

        <meta
          key="og:title"
          name="og:title"
          property="og:title"
          content="Scientific Poster Maker: Create Academic Research Posters | Mind the Graph"
        />
        <meta
          key="og:description"
          name="og:description"
          property="og:description"
          content="Scientific poster maker to present your research with free designs, templates and guides. Professional research poster maker and academic poster maker by Mind the Graph."
        />
        <meta
          key="og:image"
          name="og:image"
          property="og:image"
          content="/images/poster/poster-maker-share.jpg"
        />
        <meta key="og:image:width" name="og:image:width" property="og:image:width" content="450" />
        <meta
          key="og:image:height"
          name="og:image:height"
          property="og:image:height"
          content="286"
        />

        <meta
          key="twitter:card"
          name="twitter:card"
          property="twitter:card"
          content="summary_large_image"
        />
        <meta
          key="twitter:title"
          name="twitter:title"
          property="twitter:title"
          content="Scientific Poster Maker: Create Academic Research Posters | Mind the Graph"
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          property="twitter:description"
          content="Scientific poster maker to present your research with free designs, templates and guides. Professional research poster maker and academic poster maker by Mind the Graph."
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          property="twitter:image"
          content="/images/poster/poster-maker-share.jpg"
        />
        <meta
          key="twitter:image:alt"
          name="twitter:image:alt"
          property="twitter:image:alt"
          content="Create scientific posters"
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>

      <div className={classes.primaryBg}>
        <Container>
          <Box pt={4} pb={10}>
            <div>
              <Link href={getEnv('MTG_URL_APP')} target="_blank" underline="hover">
                <Image
                  className={classes.brandLogo}
                  src="/images/mtg-logo-editage.svg"
                  height={43}
                  width={130}
                  alt="Mind The Graph Logo"
                  title="Mind The Graph Logo"
                  loading="eager"
                />
              </Link>
            </div>

            <Grid container={true} spacing={2} alignItems="center">
              <Grid item={true} xs={12} md={6}>
                <Box px={6}>
                  <Typography
                    id="header"
                    className={classes.headerTitle}
                    gutterBottom={true}
                    component="h1"
                    variant="h4"
                  >
                    Eye-catching &nbsp; <br /> scientific poster maker for &nbsp; <br /> researchers
                    and academics
                  </Typography>

                  <Typography style={{ color: 'white', fontWeight: 'bold' }}>
                    Our scientific poster maker guides you through every step to create stunning
                    academic research posters in minutes. With our easy-to-use research poster tool
                    and scientifically accurate illustrations, you can quickly design professional
                    posters and download them in print-ready formats (PNG, PDF, TIFF) perfect for
                    conferences and publications.
                  </Typography>

                  <EmailFormVariant
                    context={LandingPageContextEnum.POSTER_MAKER}
                    buttonLoggedColor="white"
                    buttonColor="white"
                    textButtonLogged="Start Creating Now"
                    labelColor="white"
                    textAgreeColor="white"
                  />
                </Box>
              </Grid>
              <Grid item={true} xs={12} md={6}>
                <div className={classes.likeMagicBox}>
                  <div className={classes.likeMagicBoxItem}>
                    <Image
                      src="/images/poster/like-magic.png"
                      height={60}
                      width={250}
                      alt="Create scientific posters like magic"
                      loading="eager"
                    />
                  </div>

                  <Divider
                    className={classes.verticalDivider}
                    orientation="vertical"
                    flexItem={true}
                  />

                  <div className={classes.likeMagicBoxItem}>
                    <Typography>
                      <b>Create beautiful scientific posters with no effort</b>
                    </Typography>
                  </div>
                </div>

                <Image
                  className={classes.sectionImage}
                  src="/images/poster/poster-header.png"
                  height={394}
                  width={600}
                  alt="Eye-catching scientific posters in a few clicks"
                  loading="eager"
                />
              </Grid>
            </Grid>
          </Box>
        </Container>
      </div>

      <MainContent onActionClick={scrollActionClick} />
    </>
  );
}
